import Lightpick from 'lightpick';
import weekdaysData from '../DateRangePickerWeekdays.json';
import Announcer from './Announcer';
export default class Calendar {
    constructor(config) {
        this.INITIAL_SETUP_DELAY = 20;
        this.MONTH_CHANGE_DELAY = 50;
        this.DOM_UPDATE_DELAY = 10;
        this.SELECT_REORDER_DELAY = 10;
        this.MUTATION_OBSERVER_DELAY = 10;
        this.ANNOUNCEMENT_DELAY = 10;
        this.FOCUS_DELAY = 50;
        this.DAYS_IN_WEEK = 7;
        this.observer = null;
        this.pickerElement = null;
        this.isRangePicker = false;
        this.announcer = null;
        this.focusedElementBeforeMonthChange = null;
        this.shouldRestoreFocus = false;
        this.isProgrammaticNavigation = false;
        const originalOnMonthChange = config.onMonthChange;
        const originalOnSelect = config.onSelect;
        this.isRangePicker = !config.singleDate;
        this.picker = new Lightpick(Object.assign(Object.assign(Object.assign({}, this.getDefaultConfig()), config), { onOpen: () => {
                this.overrideWeekdayNames();
                setTimeout(() => {
                    this.applyAccessibility();
                    this.overrideWeekdayNames();
                    this.applyGridAccessibility();
                    this.applyNavigationAccessibility();
                    this.startObserving();
                    this.attachNavigationKeyboardHandlers();
                    this.removeCloseButton();
                    if (this.isRangePicker) {
                        this.setInitialFocus();
                    }
                }, this.INITIAL_SETUP_DELAY);
                if (config.onOpen)
                    config.onOpen();
            }, onClose: () => {
                this.stopObserving();
                if (config.onClose)
                    config.onClose();
            }, onMonthChange: (month, year) => {
                this.overrideWeekdayNames();
                setTimeout(() => {
                    this.overrideWeekdayNames();
                    this.applyGridAccessibility();
                    this.applySelectAccessibility();
                    this.applyNavigationAccessibility();
                    this.attachNavigationKeyboardHandlers();
                    this.removeCloseButton();
                }, this.MONTH_CHANGE_DELAY);
                if (originalOnMonthChange)
                    originalOnMonthChange(month, year);
            }, onSelect: (start, end) => {
                setTimeout(() => {
                    this.applyGridAccessibility();
                }, this.DOM_UPDATE_DELAY);
                if (originalOnSelect)
                    originalOnSelect(start, end);
            } }));
        this.pickerElement = this.picker.el;
        // Intercept innerHTML setter to reorder selects immediately
        this.interceptInnerHTMLSetter();
        // Manually reorder selects on initial render (before interceptor was set up)
        this.reorderSelectsManually();
    }
    getDefaultConfig() {
        const currentYear = new Date().getFullYear();
        const yearsAhead = 1;
        return {
            singleDate: true,
            format: 'YYYY-MM-DD',
            numberOfMonths: 1,
            numberOfColumns: 2,
            minDate: new Date(),
            selectForward: true,
            footer: false,
            autoclose: false,
            lang: this.getLocale(),
            dropdowns: {
                years: {
                    min: 2020,
                    max: currentYear + yearsAhead,
                },
                months: true,
            },
            locale: {
                buttons: {
                    prev: '',
                    next: '',
                },
            }
        };
    }
    getLocale() {
        var _a;
        return ((_a = document.querySelector('html').getAttribute('lang')) === null || _a === void 0 ? void 0 : _a.substring(0, 2)) || 'en';
    }
    getTranslation(key, fallback) {
        var _a, _b;
        if ((_b = (_a = window.localize_script) === null || _a === void 0 ? void 0 : _a.translations[key]) !== null && _b !== void 0 ? _b : false) {
            return window.localize_script.translations[key];
        }
        return fallback;
    }
    overrideWeekdayNames() {
        const language = this.getLocale();
        const weekdayNames = weekdaysData[language] || weekdaysData.en;
        const weekdayElements = (this.pickerElement || document).querySelectorAll('.lightpick__day-of-the-week');
        weekdayElements.forEach((element, index) => {
            element.textContent = weekdayNames[index];
        });
    }
    applyGridAccessibility() {
        var _a, _b;
        if (!this.pickerElement)
            return;
        const dayElements = this.pickerElement.querySelectorAll('.lightpick__day');
        const rows = [];
        let currentRow = [];
        dayElements.forEach((dayElement, index) => {
            currentRow.push(dayElement);
            if ((index + 1) % this.DAYS_IN_WEEK === 0) {
                rows.push(currentRow);
                currentRow = [];
            }
        });
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }
        rows.forEach((row, rowIndex) => {
            const rowDiv = row[0].parentElement;
            if (rowDiv) {
                rowDiv.setAttribute('role', 'row');
            }
            row.forEach((dayElement) => {
                const div = dayElement;
                div.setAttribute('role', 'gridcell');
                div.setAttribute('tabindex', '-1');
                if (!div.classList.contains('is-disabled')) {
                    div.setAttribute('tabindex', '0');
                }
                const timestamp = div.getAttribute('data-time');
                if (timestamp) {
                    const date = new Date(parseInt(timestamp));
                    const dateString = date.toLocaleDateString(this.getLocale(), {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                    div.setAttribute('aria-label', dateString);
                }
                if (div.classList.contains('is-disabled')) {
                    div.setAttribute('aria-disabled', 'true');
                }
                if (div.classList.contains('is-start-date') || div.classList.contains('is-end-date')) {
                    div.setAttribute('aria-selected', 'true');
                }
                else if (div.classList.contains('is-in-range')) {
                    div.setAttribute('aria-selected', 'true');
                }
                else {
                    div.setAttribute('aria-selected', 'false');
                }
                if (!div.hasAttribute('data-accessibility-applied')) {
                    div.addEventListener('keydown', (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            div.dispatchEvent(new Event('mousedown', { bubbles: true }));
                        }
                    });
                    div.setAttribute('data-accessibility-applied', 'true');
                }
            });
        });
        const daysContainer = this.pickerElement.querySelector('.lightpick__days');
        if (daysContainer) {
            // Check if already wrapped with role=grid
            const parentHasGridRole = ((_a = daysContainer.parentElement) === null || _a === void 0 ? void 0 : _a.getAttribute('role')) === 'grid';
            if (!parentHasGridRole) {
                const gridWrapper = document.createElement('div');
                gridWrapper.setAttribute('role', 'grid');
                gridWrapper.setAttribute('aria-label', this.getTranslation('selectDate', 'Select Date'));
                (_b = daysContainer.parentNode) === null || _b === void 0 ? void 0 : _b.insertBefore(gridWrapper, daysContainer);
                gridWrapper.appendChild(daysContainer);
            }
        }
        this.attachGridFocusListener();
    }
    attachGridFocusListener() {
        if (!this.pickerElement)
            return;
        if (this.pickerElement.gridFocusListenerAttached === true) {
            return;
        }
        const focusHandler = (event) => {
            const target = event.target;
            if (target && target.classList && target.classList.contains('lightpick__day')) {
                this.ensureAnnouncerReady();
                this.announceDate(target);
            }
        };
        this.pickerElement.addEventListener('focusin', focusHandler);
        const globalFocusHandler = (event) => {
            const target = event.target;
            if (target &&
                target.classList &&
                target.classList.contains('lightpick__day') &&
                this.pickerElement &&
                this.pickerElement.contains(target)) {
                this.ensureAnnouncerReady();
                this.announceDate(target);
            }
        };
        document.addEventListener('focusin', globalFocusHandler);
        this.pickerElement.gridFocusListenerAttached = true;
        this.pickerElement.globalFocusHandler = globalFocusHandler; // Store reference for cleanup
    }
    ensureAnnouncerReady() {
        var _a;
        if (!this.announcer) {
            this.initializeAnnouncer();
        }
        (_a = this.announcer) === null || _a === void 0 ? void 0 : _a.ensureReady();
    }
    startObserving() {
        if (!this.pickerElement || this.observer)
            return;
        this.observer = new MutationObserver((mutations) => {
            this.handleMutations(mutations);
        });
        this.observer.observe(this.pickerElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });
    }
    handleMutations(mutations) {
        let shouldUpdate = false;
        let shouldUpdateAccessibility = false;
        mutations.forEach((mutation) => {
            if (this.isCalendarContentMutation(mutation)) {
                shouldUpdate = true;
                shouldUpdateAccessibility = true;
            }
            if (this.isDayAttributeMutation(mutation)) {
                shouldUpdate = true;
            }
            if (this.isHeaderMutation(mutation)) {
                shouldUpdate = true;
                shouldUpdateAccessibility = true;
            }
        });
        if (shouldUpdate) {
            this.updateCalendarAccessibility(shouldUpdateAccessibility);
        }
    }
    isCalendarContentMutation(mutation) {
        if (mutation.type !== 'childList')
            return false;
        const addedNodes = Array.from(mutation.addedNodes);
        return addedNodes.some(node => node.nodeType === Node.ELEMENT_NODE &&
            (node.classList.contains('lightpick__month') ||
                node.classList.contains('lightpick__day') ||
                node.classList.contains('lightpick__previous-action') ||
                node.classList.contains('lightpick__next-action') ||
                node.classList.contains('lightpick__close-action')));
    }
    isDayAttributeMutation(mutation) {
        return mutation.type === 'attributes' &&
            mutation.target instanceof Element &&
            mutation.target.classList.contains('lightpick__day');
    }
    isHeaderMutation(mutation) {
        return mutation.type === 'childList' &&
            mutation.target instanceof Element &&
            (mutation.target.classList.contains('lightpick__month-title') ||
                mutation.target.classList.contains('lightpick__header'));
    }
    updateCalendarAccessibility(shouldUpdateAccessibility) {
        setTimeout(() => {
            this.overrideWeekdayNames();
            this.applyGridAccessibility();
            this.removeCloseButton();
            if (shouldUpdateAccessibility) {
                this.applySelectAccessibility();
                this.applyNavigationAccessibility();
            }
            this.restoreFocusAfterMonthChange();
        }, this.MUTATION_OBSERVER_DELAY);
    }
    stopObserving() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
    restoreFocusAfterMonthChange() {
        if (!this.focusedElementBeforeMonthChange || !this.shouldRestoreFocus)
            return;
        const previousClass = this.focusedElementBeforeMonthChange.className;
        // For navigation buttons, announce the current month and year
        if (previousClass.includes('lightpick__previous-action') ||
            previousClass.includes('lightpick__next-action')) {
            this.announceCurrentMonth();
        }
        // Restore focus without timeout first
        this.restoreFocusToElement(previousClass);
        // Also restore focus with timeout as fallback
        setTimeout(() => {
            this.restoreFocusToElement(previousClass);
            this.focusedElementBeforeMonthChange = null;
            this.shouldRestoreFocus = false;
        }, this.FOCUS_DELAY);
    }
    announceCurrentMonth() {
        var _a;
        const monthTitle = (_a = this.pickerElement) === null || _a === void 0 ? void 0 : _a.querySelector('.lightpick__month-title');
        if (monthTitle && monthTitle.textContent) {
            this.ensureAnnouncerReady();
            setTimeout(() => {
                this.announce(monthTitle.textContent || '');
            }, this.ANNOUNCEMENT_DELAY);
        }
    }
    restoreFocusToElement(previousClass) {
        var _a, _b, _c;
        if (previousClass.includes('lightpick__previous-action')) {
            const prevButton = (_a = this.pickerElement) === null || _a === void 0 ? void 0 : _a.querySelector('.lightpick__previous-action');
            if (prevButton) {
                prevButton.focus();
            }
        }
        else if (previousClass.includes('lightpick__next-action')) {
            const nextButton = (_b = this.pickerElement) === null || _b === void 0 ? void 0 : _b.querySelector('.lightpick__next-action');
            if (nextButton) {
                nextButton.focus();
            }
        }
        else if (previousClass.includes('lightpick__select-months')) {
            const monthSelect = (_c = this.pickerElement) === null || _c === void 0 ? void 0 : _c.querySelector('.lightpick__select-months');
            if (monthSelect) {
                monthSelect.focus();
            }
        }
    }
    setInitialFocus() {
        const monthSelector = (this.pickerElement || document).querySelector('.lightpick__select-years');
        if (monthSelector) {
            monthSelector.focus();
        }
    }
    attachNavigationKeyboardHandlers() {
        if (!this.pickerElement)
            return;
        const container = this.pickerElement;
        const handleKeydown = (event) => {
            const target = event.target;
            // Prevent default page movement for Page Up/Down when calendar is open
            if (event.key === 'PageUp' || event.key === 'PageDown') {
                event.preventDefault();
            }
            // Find the actual day button (might be the target or a parent)
            const dayButton = this.findDayButton(target);
            if (event.key === 'Enter' || event.key === ' ') {
                if (target.classList.contains('lightpick__previous-action') || target.classList.contains('lightpick__next-action')) {
                    event.preventDefault();
                    this.focusedElementBeforeMonthChange = target;
                    this.shouldRestoreFocus = true;
                    target.dispatchEvent(new Event('mousedown', { bubbles: true }));
                }
                else if (dayButton && !dayButton.hasAttribute('disabled')) {
                    event.preventDefault();
                    this.announceSelectedDate(dayButton);
                    dayButton.dispatchEvent(new Event('mousedown', { bubbles: true }));
                }
            }
            if (dayButton && !dayButton.hasAttribute('disabled')) {
                this.handleArrowKeyNavigation(event, dayButton);
            }
            else if (event.key === 'PageUp' || event.key === 'PageDown') {
                // Check if a navigation button or month select is currently focused
                if (target.classList.contains('lightpick__previous-action') ||
                    target.classList.contains('lightpick__next-action') ||
                    target.classList.contains('lightpick__select-months')) {
                    this.focusedElementBeforeMonthChange = target;
                    this.shouldRestoreFocus = true;
                }
                else {
                    this.shouldRestoreFocus = false;
                }
                this.handleMonthNavigation(event);
            }
        };
        if (container.dataset.keyboardHandlerAttached !== 'true') {
            container.addEventListener('keydown', handleKeydown, true);
            container.dataset.keyboardHandlerAttached = 'true';
        }
        if (container.dataset.clickHandlerAttached !== 'true') {
            container.addEventListener('mousedown', (event) => {
                const target = event.target;
                const parent = target.parentElement;
                if (target.classList.contains('lightpick__previous-action') ||
                    target.classList.contains('lightpick__next-action')) {
                    this.focusedElementBeforeMonthChange = target;
                    // Only restore focus if this is not programmatic navigation
                    this.shouldRestoreFocus = !this.isProgrammaticNavigation;
                }
                else if (parent && (parent.classList.contains('lightpick__previous-action') ||
                    parent.classList.contains('lightpick__next-action'))) {
                    this.focusedElementBeforeMonthChange = parent;
                    // Only restore focus if this is not programmatic navigation
                    this.shouldRestoreFocus = !this.isProgrammaticNavigation;
                }
            }, true);
            container.addEventListener('change', (event) => {
                const target = event.target;
                if (target.classList.contains('lightpick__select-months')) {
                    this.focusedElementBeforeMonthChange = target;
                    this.shouldRestoreFocus = true;
                }
            }, true);
            container.dataset.clickHandlerAttached = 'true';
        }
        this.applyNavigationAccessibility();
    }
    findDayButton(element) {
        if (element.classList.contains('lightpick__day')) {
            return element;
        }
        const parent = element.closest('.lightpick__day');
        if (parent) {
            return parent;
        }
        return null;
    }
    handleArrowKeyNavigation(event, currentButton) {
        let targetButton = null;
        let monthNavigated = false;
        switch (event.key) {
            case 'ArrowRight':
                const nextDayResult = this.getNextDay(currentButton);
                targetButton = (nextDayResult === null || nextDayResult === void 0 ? void 0 : nextDayResult.button) || null;
                monthNavigated = (nextDayResult === null || nextDayResult === void 0 ? void 0 : nextDayResult.monthNavigated) || false;
                break;
            case 'ArrowLeft':
                const prevDayResult = this.getPreviousDay(currentButton);
                targetButton = (prevDayResult === null || prevDayResult === void 0 ? void 0 : prevDayResult.button) || null;
                monthNavigated = (prevDayResult === null || prevDayResult === void 0 ? void 0 : prevDayResult.monthNavigated) || false;
                break;
            case 'ArrowDown':
                targetButton = this.getDayBelow(currentButton, this.DAYS_IN_WEEK);
                if (!targetButton && this.isInLastWeek(currentButton)) {
                    this.navigateToSameDay(1, currentButton);
                    monthNavigated = true;
                }
                break;
            case 'ArrowUp':
                targetButton = this.getDayAbove(currentButton, this.DAYS_IN_WEEK);
                if (!targetButton && this.isInFirstWeek(currentButton)) {
                    this.navigateToSameDay(-1, currentButton);
                    monthNavigated = true;
                }
                break;
            case 'Home':
                targetButton = this.getFirstDayOfWeek(currentButton);
                break;
            case 'End':
                targetButton = this.getLastDayOfWeek(currentButton);
                break;
            case 'PageUp':
                if (event.shiftKey) {
                    this.navigateToSameYear(-1, currentButton);
                }
                else {
                    this.navigateToSameDay(-1, currentButton);
                }
                event.preventDefault();
                return;
            case 'PageDown':
                if (event.shiftKey) {
                    this.navigateToSameYear(1, currentButton);
                }
                else {
                    this.navigateToSameDay(1, currentButton);
                }
                event.preventDefault();
                return;
            default:
                return;
        }
        if (targetButton || monthNavigated) {
            event.preventDefault();
            if (targetButton) {
                targetButton.focus();
                this.announceDate(targetButton);
            }
        }
    }
    announceDate(dayElement) {
        if (!dayElement)
            return;
        let ariaLabel = dayElement.getAttribute('aria-label');
        if (!ariaLabel) {
            const timestamp = dayElement.getAttribute('data-time');
            if (timestamp) {
                const date = new Date(parseInt(timestamp));
                ariaLabel = date.toLocaleDateString(this.getLocale(), {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
                dayElement.setAttribute('aria-label', ariaLabel);
            }
        }
        if (ariaLabel) {
            this.ensureAnnouncerReady();
            setTimeout(() => {
                this.announce(ariaLabel);
            }, this.ANNOUNCEMENT_DELAY);
        }
    }
    announceSelectedDate(dayElement) {
        if (!dayElement)
            return;
        let ariaLabel = dayElement.getAttribute('aria-label');
        if (!ariaLabel) {
            const timestamp = dayElement.getAttribute('data-time');
            if (timestamp) {
                const date = new Date(parseInt(timestamp));
                ariaLabel = date.toLocaleDateString(this.getLocale(), {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        }
        if (ariaLabel) {
            this.ensureAnnouncerReady();
            const dateSelectedLabel = this.getTranslation('dateSelected', 'Date selected');
            const announcement = `${ariaLabel}, ${dateSelectedLabel}`;
            setTimeout(() => {
                this.announce(announcement);
            }, this.ANNOUNCEMENT_DELAY);
        }
    }
    handleMonthNavigation(event) {
        let currentDate = null;
        const activeElement = document.activeElement;
        if (activeElement && activeElement.classList.contains('lightpick__day')) {
            const timestamp = activeElement.getAttribute('data-time');
            if (timestamp) {
                currentDate = new Date(parseInt(timestamp));
            }
        }
        if (!currentDate) {
            const pickerDate = this.picker.getStartDate();
            if (pickerDate) {
                currentDate = pickerDate.toDate();
            }
        }
        if (!currentDate) {
            const allDays = this.getAllDayElements().filter(day => !day.hasAttribute('disabled'));
            if (allDays.length > 0) {
                const timestamp = allDays[0].getAttribute('data-time');
                if (timestamp) {
                    currentDate = new Date(parseInt(timestamp));
                }
            }
        }
        if (event.key === 'PageUp') {
            if (event.shiftKey) {
                this.navigateMonth(-12, currentDate);
            }
            else {
                this.navigateMonth(-1, currentDate);
            }
        }
        else if (event.key === 'PageDown') {
            if (event.shiftKey) {
                this.navigateMonth(12, currentDate);
            }
            else {
                this.navigateMonth(1, currentDate);
            }
        }
    }
    navigateMonth(monthOffset, currentDate) {
        var _a, _b;
        let dateToUse = currentDate || null;
        if (!dateToUse) {
            const pickerDate = this.picker.getStartDate();
            if (pickerDate) {
                dateToUse = pickerDate.toDate();
            }
        }
        const prevButton = (_a = this.pickerElement) === null || _a === void 0 ? void 0 : _a.querySelector('.lightpick__previous-action');
        const nextButton = (_b = this.pickerElement) === null || _b === void 0 ? void 0 : _b.querySelector('.lightpick__next-action');
        if (monthOffset < 0 && prevButton) {
            for (let i = 0; i < Math.abs(monthOffset); i++) {
                prevButton.dispatchEvent(new Event('mousedown', { bubbles: true }));
            }
        }
        else if (monthOffset > 0 && nextButton) {
            for (let i = 0; i < monthOffset; i++) {
                nextButton.dispatchEvent(new Event('mousedown', { bubbles: true }));
            }
        }
        if (dateToUse) {
            const targetDay = dateToUse.getDate();
            const targetMonth = dateToUse.getMonth() + monthOffset;
            const targetYear = dateToUse.getFullYear() + Math.floor(targetMonth / 12);
            const normalizedMonth = ((targetMonth % 12) + 12) % 12;
            const delay = Math.abs(monthOffset) > 1 ? this.MONTH_CHANGE_DELAY * Math.abs(monthOffset) : this.MONTH_CHANGE_DELAY + this.FOCUS_DELAY;
            setTimeout(() => {
                this.focusDayInMonth(targetYear, normalizedMonth, targetDay);
            }, delay);
        }
    }
    getNextDay(currentButton) {
        let next = currentButton.nextElementSibling;
        while (next && next.classList.contains('lightpick__day')) {
            if (!next.hasAttribute('disabled')) {
                return { button: next, monthNavigated: false };
            }
            next = next.nextElementSibling;
        }
        const nextRowDay = this.getFirstDayOfNextRow(currentButton);
        if (nextRowDay) {
            return { button: nextRowDay, monthNavigated: false };
        }
        if (this.isLastEnabledDay(currentButton)) {
            this.navigateToSameDay(1, currentButton);
            return { button: null, monthNavigated: true };
        }
        return { button: null, monthNavigated: false };
    }
    getPreviousDay(currentButton) {
        let prev = currentButton.previousElementSibling;
        while (prev && prev.classList.contains('lightpick__day')) {
            if (!prev.hasAttribute('disabled')) {
                return { button: prev, monthNavigated: false };
            }
            prev = prev.previousElementSibling;
        }
        const prevRowDay = this.getLastDayOfPreviousRow(currentButton);
        if (prevRowDay) {
            return { button: prevRowDay, monthNavigated: false };
        }
        if (this.isFirstEnabledDay(currentButton)) {
            this.navigateToSameDay(-1, currentButton);
            return { button: null, monthNavigated: true };
        }
        return { button: null, monthNavigated: false };
    }
    getDayBelow(currentButton, offset) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        for (let i = currentIndex + offset; i < days.length; i++) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return null;
    }
    getDayAbove(currentButton, offset) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        for (let i = currentIndex - offset; i >= 0; i--) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return null;
    }
    getFirstDayOfWeek(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        const weekStart = this.getWeekStart(currentIndex);
        for (let i = weekStart; i < currentIndex; i++) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return currentButton;
    }
    getLastDayOfWeek(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        const weekEnd = this.getWeekEnd(currentIndex);
        for (let i = Math.min(weekEnd, days.length - 1); i > currentIndex; i--) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return currentButton;
    }
    getFirstDayOfNextRow(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        const nextRowStart = this.getNextRowStart(currentIndex);
        for (let i = nextRowStart; i < days.length; i++) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return null;
    }
    getLastDayOfPreviousRow(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return null;
        const prevRowEnd = this.getPreviousRowEnd(currentIndex);
        for (let i = prevRowEnd; i >= 0; i--) {
            if (!days[i].hasAttribute('disabled')) {
                return days[i];
            }
        }
        return null;
    }
    getAllDayElements() {
        if (!this.pickerElement)
            return [];
        return Array.from(this.pickerElement.querySelectorAll('.lightpick__day'));
    }
    isLastEnabledDay(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return false;
        for (let i = currentIndex + 1; i < days.length; i++) {
            if (!days[i].hasAttribute('disabled')) {
                return false;
            }
        }
        return true;
    }
    isFirstEnabledDay(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return false;
        for (let i = 0; i < currentIndex; i++) {
            if (!days[i].hasAttribute('disabled')) {
                return false;
            }
        }
        return true;
    }
    navigateToSameDay(monthOffset, currentButton) {
        var _a, _b;
        let currentDate = null;
        if (currentButton) {
            const timestamp = currentButton.getAttribute('data-time');
            if (timestamp) {
                currentDate = new Date(parseInt(timestamp));
            }
        }
        if (!currentDate) {
            const pickerDate = this.picker.getStartDate();
            if (pickerDate) {
                currentDate = pickerDate.toDate();
            }
        }
        if (!currentDate) {
            return;
        }
        const targetDate = new Date(currentDate);
        targetDate.setMonth(targetDate.getMonth() + monthOffset);
        const prevButton = (_a = this.pickerElement) === null || _a === void 0 ? void 0 : _a.querySelector('.lightpick__previous-action');
        const nextButton = (_b = this.pickerElement) === null || _b === void 0 ? void 0 : _b.querySelector('.lightpick__next-action');
        this.isProgrammaticNavigation = true;
        if (monthOffset < 0 && prevButton) {
            for (let i = 0; i < Math.abs(monthOffset); i++) {
                prevButton.dispatchEvent(new Event('mousedown', { bubbles: true }));
            }
        }
        else if (monthOffset > 0 && nextButton) {
            for (let i = 0; i < monthOffset; i++) {
                nextButton.dispatchEvent(new Event('mousedown', { bubbles: true }));
            }
        }
        this.isProgrammaticNavigation = false;
        setTimeout(() => {
            this.focusDayByDate(targetDate);
        }, this.MONTH_CHANGE_DELAY + this.FOCUS_DELAY);
    }
    navigateToSameYear(yearOffset, currentButton) {
        let currentDate = null;
        if (currentButton) {
            const timestamp = currentButton.getAttribute('data-time');
            if (timestamp) {
                currentDate = new Date(parseInt(timestamp));
            }
        }
        if (!currentDate) {
            const pickerDate = this.picker.getStartDate();
            if (pickerDate) {
                currentDate = pickerDate.toDate();
            }
        }
        if (!currentDate) {
            return;
        }
        const targetYear = currentDate.getFullYear() + yearOffset;
        const targetMonth = currentDate.getMonth();
        const targetDay = currentDate.getDate();
        if (!this.pickerElement)
            return;
        const yearSelect = this.pickerElement.querySelector('.lightpick__select-years');
        if (!yearSelect)
            return;
        // Get valid year range from select options
        const yearOptions = Array.from(yearSelect.options).map(option => parseInt(option.value));
        const minYear = Math.min(...yearOptions);
        const maxYear = Math.max(...yearOptions);
        // Constrain target year to valid range
        const constrainedYear = Math.max(minYear, Math.min(maxYear, targetYear));
        yearSelect.value = String(constrainedYear);
        yearSelect.dispatchEvent(new Event('change', { bubbles: true }));
        const delay = this.MONTH_CHANGE_DELAY + this.FOCUS_DELAY;
        setTimeout(() => {
            this.focusDayInMonth(constrainedYear, targetMonth, targetDay);
        }, delay);
    }
    focusDayByDate(date) {
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        const timestamp = normalizedDate.getTime();
        const allDays = this.getAllDayElements();
        let targetDay = null;
        for (const day of allDays) {
            const dayTimestamp = day.getAttribute('data-time');
            if (dayTimestamp === String(timestamp) && !day.hasAttribute('disabled')) {
                targetDay = day;
                break;
            }
        }
        if (targetDay) {
            targetDay.focus();
            this.announceDate(targetDay);
            return;
        }
        const targetDayOfMonth = normalizedDate.getDate();
        for (const day of allDays) {
            const dayTimestamp = day.getAttribute('data-time');
            if (dayTimestamp) {
                const dayDate = new Date(parseInt(dayTimestamp));
                if (dayDate.getDate() === targetDayOfMonth && !day.hasAttribute('disabled')) {
                    day.focus();
                    this.announceDate(day);
                    return;
                }
            }
        }
        const enabledDays = allDays.filter(day => !day.hasAttribute('disabled'));
        if (enabledDays.length > 0) {
            enabledDays[enabledDays.length - 1].focus();
            this.announceDate(enabledDays[enabledDays.length - 1]);
        }
    }
    focusDayInMonth(year, month, day) {
        const allDays = this.getAllDayElements();
        for (const dayElement of allDays) {
            const dayTimestamp = dayElement.getAttribute('data-time');
            if (dayTimestamp) {
                const dayDate = new Date(parseInt(dayTimestamp));
                if (dayDate.getFullYear() === year &&
                    dayDate.getMonth() === month &&
                    dayDate.getDate() === day &&
                    !dayElement.hasAttribute('disabled')) {
                    dayElement.focus();
                    this.announceDate(dayElement);
                    return;
                }
            }
        }
        for (const dayElement of allDays) {
            const dayTimestamp = dayElement.getAttribute('data-time');
            if (dayTimestamp) {
                const dayDate = new Date(parseInt(dayTimestamp));
                if (dayDate.getFullYear() === year &&
                    dayDate.getMonth() === month &&
                    !dayElement.hasAttribute('disabled')) {
                    dayElement.focus();
                    this.announceDate(dayElement);
                    return;
                }
            }
        }
        const enabledDays = allDays.filter(dayElement => !dayElement.hasAttribute('disabled'));
        if (enabledDays.length > 0) {
            enabledDays[enabledDays.length - 1].focus();
            this.announceDate(enabledDays[enabledDays.length - 1]);
        }
    }
    applyNavigationAccessibility() {
        if (!this.pickerElement)
            return;
        const previousButton = this.pickerElement.querySelector('.lightpick__previous-action');
        const nextButton = this.pickerElement.querySelector('.lightpick__next-action');
        const prevMonthLabel = this.getTranslation('previousMonth', 'Previous month');
        const nextMonthLabel = this.getTranslation('nextMonth', 'Next month');
        if (previousButton) {
            previousButton.setAttribute('role', 'button');
            previousButton.setAttribute('tabindex', '0');
            previousButton.setAttribute('aria-label', prevMonthLabel);
        }
        if (nextButton) {
            nextButton.setAttribute('role', 'button');
            nextButton.setAttribute('tabindex', '0');
            nextButton.setAttribute('aria-label', nextMonthLabel);
        }
    }
    applyAccessibility() {
        if (!this.pickerElement)
            return;
        this.pickerElement.setAttribute('aria-label', this.getTranslation('selectDate', 'Select Date'));
        if (this.isRangePicker) {
            this.pickerElement.setAttribute('role', 'dialog');
            this.pickerElement.setAttribute('aria-modal', 'true');
        }
        this.initializeAnnouncer();
    }
    initializeAnnouncer() {
        if (!this.pickerElement)
            return;
        if (this.announcer)
            return;
        this.announcer = new Announcer(this.pickerElement);
    }
    announce(message) {
        if (!this.announcer) {
            return;
        }
        this.announcer.announce(message);
    }
    interceptInnerHTMLSetter() {
        if (!this.pickerElement)
            return;
        const monthsContainer = this.pickerElement.querySelector('.lightpick__months');
        if (!monthsContainer)
            return;
        // Store the original innerHTML descriptor
        const originalDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
        if (!originalDescriptor)
            return;
        // Override innerHTML for this specific element
        Object.defineProperty(monthsContainer, 'innerHTML', {
            set: (value) => {
                var _a;
                // Swap the order of selects in the HTML string before setting
                const modifiedValue = this.swapSelectsInHTML(value);
                (_a = originalDescriptor.set) === null || _a === void 0 ? void 0 : _a.call(monthsContainer, modifiedValue);
            },
            get: () => {
                var _a;
                return (_a = originalDescriptor.get) === null || _a === void 0 ? void 0 : _a.call(monthsContainer);
            }
        });
    }
    swapSelectsInHTML(html) {
        const monthTitleOpen = '(<div class="lightpick__month-title">)';
        const beforeMonths = '([\\s\\S]*?)';
        const monthsSelect = '(<select[\\s\\S]*?lightpick__select-months[\\s\\S]*?<\\/select>)';
        const betweenSelects = '([\\s\\S]*?)';
        const yearsSelect = '(<select[\\s\\S]*?lightpick__select-years[\\s\\S]*?<\\/select>)';
        const regex = new RegExp(monthTitleOpen + beforeMonths + monthsSelect + betweenSelects + yearsSelect, 'g');
        return html.replace(regex, (match, openDiv, before, months, between, years) => {
            setTimeout(() => {
                this.applySelectAccessibility();
            }, this.SELECT_REORDER_DELAY);
            return openDiv + before + years + between + months;
        });
    }
    reorderSelectsManually() {
        if (!this.pickerElement)
            return;
        const monthTitle = this.pickerElement.querySelector('.lightpick__month-title');
        if (!monthTitle)
            return;
        const monthsSelect = monthTitle.querySelector('.lightpick__select-months');
        const yearsSelect = monthTitle.querySelector('.lightpick__select-years');
        if (monthsSelect && yearsSelect && monthsSelect.previousElementSibling !== yearsSelect) {
            monthTitle.insertBefore(yearsSelect, monthsSelect);
        }
        this.applySelectAccessibility();
    }
    applySelectAccessibility() {
        if (!this.pickerElement)
            return;
        const monthTitle = this.pickerElement.querySelector('.lightpick__month-title');
        if (!monthTitle)
            return;
        const monthsSelect = monthTitle.querySelector('.lightpick__select-months');
        const yearsSelect = monthTitle.querySelector('.lightpick__select-years');
        const selectMonthLabel = this.getTranslation('selectMonth', 'Select month');
        const selectYearLabel = this.getTranslation('selectYear', 'Select year');
        if (monthsSelect) {
            monthsSelect.setAttribute('name', 'calendar_month');
            monthsSelect.setAttribute('aria-label', selectMonthLabel);
        }
        if (yearsSelect) {
            yearsSelect.setAttribute('name', 'calendar_year');
            yearsSelect.setAttribute('aria-label', selectYearLabel);
        }
    }
    getPicker() {
        return this.picker;
    }
    destroy() {
        this.stopObserving();
        // Clean up announcer
        if (this.announcer) {
            this.announcer.destroy();
            this.announcer = null;
        }
        // Clean up global focus listener
        if (this.pickerElement && this.pickerElement.globalFocusHandler) {
            document.removeEventListener('focusin', this.pickerElement.globalFocusHandler);
        }
        if (this.picker) {
            this.picker.destroy();
        }
    }
    setDate(date) {
        this.picker.setDate(date);
    }
    setDateRange(start, end) {
        this.picker.setDateRange(start, end);
    }
    hide() {
        this.picker.hide();
    }
    show() {
        this.picker.show();
    }
    getWeekStart(currentIndex) {
        return Math.floor(currentIndex / this.DAYS_IN_WEEK) * this.DAYS_IN_WEEK;
    }
    getWeekEnd(currentIndex) {
        return this.getWeekStart(currentIndex) + this.DAYS_IN_WEEK - 1;
    }
    getNextRowStart(currentIndex) {
        return this.getWeekStart(currentIndex) + this.DAYS_IN_WEEK;
    }
    getPreviousRowEnd(currentIndex) {
        return this.getWeekStart(currentIndex) - 1;
    }
    isInLastWeek(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return false;
        const nextRowStart = this.getNextRowStart(currentIndex);
        return nextRowStart >= days.length;
    }
    isInFirstWeek(currentButton) {
        const days = this.getAllDayElements();
        const currentIndex = days.indexOf(currentButton);
        if (currentIndex === -1)
            return false;
        return currentIndex < this.DAYS_IN_WEEK;
    }
    removeCloseButton() {
        var _a;
        const closeButton = (_a = this.pickerElement) === null || _a === void 0 ? void 0 : _a.querySelector('.lightpick__close-action');
        if (closeButton) {
            closeButton.remove();
        }
    }
}
